import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TosterService } from '../../services/toster.service';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-otp-varification',
  imports: [ReactiveFormsModule,CommonModule,TranslateModule],
  templateUrl: './otp-varification.component.html',
  styleUrl: './otp-varification.component.scss'
})
export class OtpVarificationComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly otpValiditySeconds = 10 * 60;
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  protected translate = inject(TranslateService)
  email:any='';
  isCustomer = false;
  otpForm: FormGroup;
  remainingSeconds = this.otpValiditySeconds;
  isResending = false;

  constructor(private fb: FormBuilder, private router: Router, private authService: AuthService, private tosterService: TosterService,private route:ActivatedRoute) {
    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.pattern('[0-9]{1}')]],
      otp2: ['', [Validators.required, Validators.pattern('[0-9]{1}')]],
      otp3: ['', [Validators.required, Validators.pattern('[0-9]{1}')]],
      otp4: ['', [Validators.required, Validators.pattern('[0-9]{1}')]],
    });
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.email =  params['email'];
      this.isCustomer = params['isCustomer'] === true || params['isCustomer'] === 'true';
    });
  }

  ngOnInit(): void {
    this.startTimer(true);
  }

  // Get form controls for validation
  get f() {
    return this.otpForm.controls;
  }

  // Auto-focus next field when current is filled
onInput(event: any, nextFieldId: string): void {
  const input = event.target as HTMLInputElement;
  const controlName = input.getAttribute('formControlName') || input.getAttribute('formcontrolname') || '';
  const normalizedValue = (input.value || '').replace(/\D/g, '').slice(0, 1);

  if (controlName) {
    this.otpForm.get(controlName)?.setValue(normalizedValue, { emitEvent: false });
  }

  input.value = normalizedValue;

  if (normalizedValue.length === 1 && nextFieldId) {
    const nextInput = this.getVisibleOtpInput(nextFieldId);
    if (nextInput) {
      nextInput.focus();
    }
  }
}

  // Handle pasting full OTP code
onPaste(event: ClipboardEvent): void {
  event.preventDefault();
  const pastedData = event.clipboardData?.getData('text') || '';
  const digits = pastedData.replace(/\D/g, '').substring(0, 4);
  
  if (digits.length > 0) {
    const otpFields = ['otp1', 'otp2', 'otp3', 'otp4'];
    for (let i = 0; i < digits.length && i < otpFields.length; i++) {
      this.otpForm.get(otpFields[i])?.setValue(digits[i]);
    }
    
    // Focus on the last filled field or first empty field
    const nextFieldIndex = Math.min(digits.length, otpFields.length - 1);
    setTimeout(() => {
      const nextInput = this.getVisibleOtpInput(otpFields[nextFieldIndex]);
      if (nextInput) {
        nextInput.focus();
      }
    }, 0);
  }
}

  private getVisibleOtpInput(controlName: string): HTMLInputElement | null {
    const matches = Array.from(
      document.querySelectorAll(`input[formcontrolname="${controlName}"]`)
    ) as HTMLInputElement[];

    if (!matches.length) {
      return null;
    }

    // Prefer the currently visible input when duplicate OTP blocks exist in DOM.
    return matches.find((el) => el.offsetParent !== null) ?? matches[0];
  }

// Handle backspace to move to previous field and delete
onKeyDown(event: KeyboardEvent, currentFieldId: string, _previousFieldId: string): void {
  if (event.key === 'Backspace') {
    event.preventDefault();

    const otpFields = ['otp1', 'otp2', 'otp3', 'otp4'];
    const currentIndex = otpFields.indexOf(currentFieldId);

    if (currentIndex === -1) {
      return;
    }

    const currentValue = this.otpForm.get(currentFieldId)?.value;

    // Always remove exactly one digit per backspace press.
    if (currentValue) {
      this.otpForm.get(currentFieldId)?.setValue('');
      return;
    }

    if (currentIndex > 0) {
      const previousId = otpFields[currentIndex - 1];
      this.otpForm.get(previousId)?.setValue('');
      const previousInput = this.getVisibleOtpInput(previousId);
      previousInput?.focus();
    }
  }
}

  get formattedTime(): string {
    const minutes = Math.floor(this.remainingSeconds / 60).toString().padStart(2, '0');
    const seconds = (this.remainingSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  get isOtpExpired(): boolean {
    return this.remainingSeconds <= 0;
  }

  get canResend(): boolean {
    return this.isOtpExpired && !this.isResending;
  }

  private startTimer(reset: boolean): void {
    if (reset) {
      this.remainingSeconds = this.otpValiditySeconds;
    }

    this.clearTimer();

    this.timerInterval = setInterval(() => {
      if (this.remainingSeconds > 0) {
        this.remainingSeconds--;
        return;
      }
      this.clearTimer();
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  resendOtp(): void {
    if (!this.canResend) {
      return;
    }

    if (!this.email) {
      this.tosterService.showError('Email is missing. Please try again.');
      return;
    }

    this.isResending = true;
    const payload = { email: this.email, isCustomer: this.isCustomer };

    this.authService.forgotPassword(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res?.status === true) {
          this.tosterService.showSuccess(res.message || 'OTP resent successfully.');
          this.otpForm.reset();
          this.startTimer(true);
        } else {
          this.tosterService.showError(res?.message || 'Unable to resend OTP.');
        }
      },
      error: (errorResponse: any) => {
        this.tosterService.showError(errorResponse?.error?.message || 'Unable to resend OTP.');
      },
      complete: () => {
        this.isResending = false;
      }
    });
  }

  verifyOtp(): void {
    if (this.isOtpExpired) {
      this.tosterService.showError('OTP expired. Please resend OTP.');
      return;
    }

    if (this.otpForm.invalid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    const otp = Object.values(this.otpForm.value).join('');
    console.log('OTP Submitted:', otp);

    const payload = { email: this.email,otp:otp };
    this.authService.otpVerifications(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res.status === true) {
          this.tosterService.showSuccess(res.message)
    this.router.navigate(['/reset-password'],
      {
        queryParams:{
          email:this.email,
          otp:otp
        }
      }
     );
         }

      },
      error: (errorResponse: any) => {
        this.tosterService.showError(errorResponse.error.message)
      },
    });
  }
    ngOnDestroy(): void {
  this.clearTimer();
  this.destroy$.next();
  this.destroy$.complete();
}
}
